/**
 * Formato de pack de tema, versionado y validado con Zod.
 *
 * Un pack es un JSON puro: nombres, salas con su género, horas y textos con
 * marcadores. No contiene lógica ni spoilers — el asesino lo decide el motor, y las
 * plantillas de pista viven en `templates.ts`. Los tres packs del MVP están en
 * `src/packs/` y se validan en un test.
 */

import { z } from 'zod';

/**
 * Versión del formato. Se incrementa con cualquier cambio incompatible (campos
 * renombrados, marcadores nuevos obligatorios…); `parseThemePack` rechaza packs de
 * otras versiones para que un pack viejo falle en la carga y no en mitad de un caso.
 */
export const PACK_FORMAT_VERSION = 1;

const genderSchema = z.enum(['m', 'f']);

const personSchema = z.strictObject({
  name: z.string().min(1),
  /** Descriptor breve con artículo: "la médica del pueblo". Para fichas de la UI. */
  descriptor: z.string().min(1),
  gender: genderSchema,
});

const roomSchema = z.strictObject({
  /** Nombre desnudo, sin artículo: "Sótano", no "el Sótano". */
  name: z.string().min(1),
  gender: genderSchema,
  /** `true` para salas de nombre plural: "Caballerizas", "Camerinos". */
  plural: z.boolean().default(false),
});

/**
 * Marcadores admitidos por campo de texto. `interpolate` (render.ts) exige que todo
 * marcador exista; aquí se valida en la otra dirección: que el pack no use marcadores
 * que el renderizador nunca va a rellenar.
 */
const ROOM_AND_HOUR = [
  'room_name',
  'room_def',
  'room_en',
  'room_al',
  'room_del',
  'hour',
];
export const PLACEHOLDERS = {
  opening: [...ROOM_AND_HOUR, 'victim', 'victim_desc'],
  'verdicts.correct': [...ROOM_AND_HOUR, 'culprit', 'culprit_desc'],
  'verdicts.wrong': [...ROOM_AND_HOUR, 'accused', 'accused_desc'],
} as const;

export function placeholdersIn(text: string): string[] {
  return [...text.matchAll(/\{([a-z_]+)\}/g)].map((m) => m[1] as string);
}

function checkPlaceholders(
  ctx: z.core.$RefinementCtx,
  field: keyof typeof PLACEHOLDERS,
  text: string,
  required: string[],
): void {
  const used = placeholdersIn(text);
  const allowed = PLACEHOLDERS[field] as readonly string[];
  for (const token of used) {
    if (!allowed.includes(token)) {
      ctx.addIssue({
        code: 'custom',
        path: field.split('.'),
        message: `marcador {${token}} no admitido en ${field}`,
      });
    }
  }
  for (const token of required) {
    if (!used.includes(token)) {
      ctx.addIssue({
        code: 'custom',
        path: field.split('.'),
        message: `${field} debe incluir el marcador {${token}}`,
      });
    }
  }
}

function checkUnique(ctx: z.core.$RefinementCtx, path: string, values: string[]): void {
  const seen = new Set<string>();
  for (const value of values) {
    if (seen.has(value)) {
      ctx.addIssue({ code: 'custom', path: [path], message: `duplicado: "${value}"` });
    }
    seen.add(value);
  }
}

export const themePackSchema = z
  .strictObject({
    formatVersion: z.literal(PACK_FORMAT_VERSION),
    /** Identificador estable del pack; es el `theme` que guarda el motor en el caso. */
    id: z.string().regex(/^[a-z0-9-]+$/),
    /** Título del caso: "Muerte en la Casa Miravé". */
    name: z.string().min(1),
    /** Una línea de ambiente para cabeceras y tarjetas. Sin spoilers. */
    tagline: z.string().min(1),
    /**
     * El tramo temporal del caso como sustantivo: "noche", "jornada", "día". Las
     * plantillas lo usan con artículo ("abrió la noche en…").
     */
    period: z.strictObject({ noun: z.string().min(1), gender: genderSchema }),
    /** La víctima no es sospechosa: no puede aparecer en `people`. */
    victim: personSchema,
    people: z.array(personSchema).min(8),
    rooms: z.array(roomSchema).min(8),
    /** Etiquetas de hora en orden de juego, formato HH:MM. Mínimo 6 (N máximo del MVP). */
    hours: z.array(z.string().regex(/^\d{2}:\d{2}$/)).min(6),
    /** Textos de ambiente sueltos para la UI. Sin spoilers. */
    ambience: z.array(z.string().min(1)).min(2),
    /** Apertura del caso: aparición del cuerpo, sala y hora. Sin spoilers. */
    opening: z.string().min(1),
    verdicts: z.strictObject({
      /** Cierre con veredicto correcto: nombra a {culprit}. */
      correct: z.string().min(1),
      /** Cierre con veredicto fallido: nombra a {accused}, nunca al culpable real. */
      wrong: z.string().min(1),
    }),
  })
  .superRefine((pack, ctx) => {
    checkUnique(
      ctx,
      'people',
      pack.people.map((p) => p.name),
    );
    checkUnique(
      ctx,
      'rooms',
      pack.rooms.map((r) => r.name),
    );
    checkUnique(ctx, 'hours', pack.hours);
    if (pack.people.some((p) => p.name === pack.victim.name)) {
      ctx.addIssue({
        code: 'custom',
        path: ['victim'],
        message: `la víctima "${pack.victim.name}" no puede estar entre los sospechosos`,
      });
    }
    checkPlaceholders(ctx, 'opening', pack.opening, ['victim', 'hour', 'room_en']);
    checkPlaceholders(ctx, 'verdicts.correct', pack.verdicts.correct, ['culprit']);
    checkPlaceholders(ctx, 'verdicts.wrong', pack.verdicts.wrong, ['accused']);
  });

export type ThemePack = z.infer<typeof themePackSchema>;
export type PackPerson = ThemePack['people'][number];
export type PackRoom = ThemePack['rooms'][number];

/** Valida un JSON desconocido y lo devuelve tipado; lanza con detalle si no cumple. */
export function parseThemePack(data: unknown): ThemePack {
  return themePackSchema.parse(data);
}
